import './startup-page-layout.css';
import './startup-card-depth.css';
import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate, useParams } from 'react-router-dom';
import { House, ChartLine, Sparkle, Calendar, Eye, UserCircle, SignOut, Buildings, Storefront, Users, CreditCard, Coins, MegaphoneSimple, ChartLineUp, Brain, List, SidebarSimple, Vault, ChartPieSlice } from '@phosphor-icons/react';
import StartupDashboardNew from './screens/StartupDashboardNew';
import Benchmarks from './screens/Benchmarks';
import MonthlyUpdateForm from './MonthlyUpdateForm';
import Interactions from './screens/Interactions';
import StartupProfile from './screens/StartupProfile';
import StartupPrograms from './screens/StartupPrograms';
import StartupServices from './screens/StartupServices';
import StartupCredit from './screens/StartupCredit';
import Studio from '../Studio';
import AgencyProfile from '../AgencyProfile';
import EngagementWorkspace from '../EngagementWorkspace';
import MetricsDashboard from '../MetricsDashboard';
import BrandBrain from '../BrandBrain';
import MarketingShell from '../MarketingShell';
import VaultDashboard from './screens/VaultDashboard';
import DataroomView from './screens/DataroomView';
import EquityDashboard from './screens/EquityDashboard';
import Team from '../Team';
import Billing from '../Billing';
import CelebrationOverlay from '../../components/CelebrationOverlay';
import StartupAICommandBar from '../../components/StartupAICommandBar';
import TrialBanner from '../../components/TrialBanner';
import OnboardingTour, { SIDEBAR_PIN_EVENT } from '../../components/OnboardingTour';
import { SidebarProfileMenu } from '../../components/SidebarProfileMenu';
import { ModuleGate, useModuleAccess } from '@/moduleAccess';
import api, { fileUrl } from '@/api';



const NavigateWithParams = ({ to }) => {
  const params = useParams();
  const resolved = Object.entries(params).reduce((acc, [k, v]) => acc.replace(`:${k}`, v), to);
  return <Navigate to={resolved} replace />;
};

// Section heading with a rule under it. Collapsed, the rail is too narrow for
// text — the rule alone keeps the grouping readable.
const SidebarSectionLabel = ({ children, expanded }) => (
  expanded ? (
    <div className="mb-2 px-2 pt-0.5 text-[9.5px] font-extrabold uppercase tracking-[0.20em] text-text-muted/85 truncate">
      {children}
    </div>
  ) : (
    <div className="mx-1 mt-1 mb-2 border-b border-border" title={children} />
  )
);

const StartupAppNew = ({ user, setUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const mainScrollRef = useRef(null);

  // The startup workspace uses its own scroll container (rather than the
  // browser window). Reset that container whenever the route changes so a
  // newly-opened page never inherits the previous page's scroll position.
  useEffect(() => {
    const scrollContainer = mainScrollRef.current;
    if (!scrollContainer) return;

    // Run after React has committed the next route's screen content.
    const frame = requestAnimationFrame(() => {
      scrollContainer.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    });

    return () => cancelAnimationFrame(frame);
  }, [location.pathname]);
  const { isEnabled } = useModuleAccess();
  const [dashboardData, setDashboardData] = useState(null);
  const [monthlySubmitted, setMonthlySubmitted] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarHovered, setSidebarHovered] = useState(false);
  const [sidebarPinned, setSidebarPinned] = useState(false); // held open by the product tour
  // Open by default on every load — collapsing is a this-session-only choice
  // via the dock toggle, not a remembered preference, independent of the
  // tour's temporary pin above.
  const [sidebarDocked, setSidebarDocked] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const sidebarExpanded = mobileSidebarOpen || sidebarHovered || sidebarPinned || sidebarDocked;

  const toggleSidebarDock = () => setSidebarDocked((prev) => !prev);

  // The product tour pins the rail open so its steps land on labelled rows.
  useEffect(() => {
    const onPin = (e) => setSidebarPinned(!!e.detail?.pinned);
    window.addEventListener(SIDEBAR_PIN_EVENT, onPin);
    return () => window.removeEventListener(SIDEBAR_PIN_EVENT, onPin);
  }, []);

  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleStartupProfileUpdate = (event) => {
      const update = event?.detail;
      if (!update || typeof update !== 'object') return;
      setDashboardData((current) => (current ? {
        ...current,
        startup: { ...(current.startup || {}), ...update },
      } : current));
    };

    window.addEventListener('founderboard:startup-profile-updated', handleStartupProfileUpdate);
    return () => window.removeEventListener('founderboard:startup-profile-updated', handleStartupProfileUpdate);
  }, []);

  useEffect(() => {
    fetchDashboardData();
    // Polling: refresh dashboard every 15s while tab is visible (picks up new acceptances/invites/ticker events)
    const tick = () => { if (document.visibilityState === 'visible') fetchDashboardData(); };
    const id = setInterval(tick, 15000);
    return () => clearInterval(id);
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [dashboardResult, updatesResult] = await Promise.allSettled([
        api.get('/startup/dashboard'), api.get('/startup/monthly-updates')
      ]);
      if (dashboardResult.status === 'fulfilled') setDashboardData(dashboardResult.value.data);
      else console.error('Failed to fetch dashboard', dashboardResult.reason);
      if (updatesResult.status === 'fulfilled') {
        const now = new Date();
        setMonthlySubmitted((updatesResult.value.data || []).some(update =>
          Number(update.month) === now.getMonth() + 1 && Number(update.year) === now.getFullYear()));
      }
    } catch (error) {
      console.error('Failed to fetch dashboard', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => { localStorage.removeItem('token'); localStorage.removeItem('user'); setUser(null); navigate('/'); };

  const startup = dashboardData?.startup;
  const latestUpdate = dashboardData?.latest_update;
  const score = dashboardData?.score || 0;
  const startupTag = [startup?.sector, startup?.funding_stage].filter(Boolean).join(' · ');

  // Keep the startup workspace navigation visible on desktop. Preserve the
  // repo's Measure / Grow / Offerings grouping, but with a roomier permanent rail.
  const navGroups = [
    {
      key: 'measure',
      label: 'Measure',
      items: [
        { path: '/startup/dashboard', icon: House, label: 'Dashboard', testId: 'nav-dashboard' },
        { path: '/startup/benchmarks', icon: ChartLine, label: 'Benchmarks', testId: 'nav-benchmarks' },
        { path: '/startup/update', icon: Calendar, label: 'Monthly Update', testId: 'nav-update', alert: monthlySubmitted !== null, alertLabel: monthlySubmitted ? 'Submitted' : 'Due', alertComplete: monthlySubmitted },
        { path: '/startup/vault', icon: Vault, label: 'Data Vault', testId: 'nav-vault' },
      ],
    },
    {
      key: 'grow',
      label: 'Grow',
      items: [
        { path: '/startup/programs', icon: Buildings, label: 'Programs', testId: 'nav-programs' },
        { path: '/startup/equity', icon: ChartPieSlice, label: 'Equity', testId: 'nav-equity' },
      ],
    },
    {
      key: 'services',
      label: 'Offerings',
      items: [
        { path: '/startup/services', icon: Storefront, label: 'Services', testId: 'nav-services', module: 'startup.services' },
        { path: '/startup/credit', icon: Coins, label: 'Founder Credit', testId: 'nav-credit', module: 'startup.credit' },
        { path: '/startup/marketing', icon: MegaphoneSimple, label: 'Marketing', testId: 'nav-marketing', module: 'startup.marketing' },
      ],
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-text-secondary">Loading...</div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-background flex flex-col overflow-hidden" style={{ height: '100dvh' }}>
      <TrialBanner />
      <div className="flex flex-1 overflow-hidden">
      {/* Mobile backdrop */}
      {mobileSidebarOpen && (
        <button
          type="button"
          aria-label="Close navigation"
          onClick={() => setMobileSidebarOpen(false)}
          className="fixed inset-0 z-40 bg-black/40 md:hidden"
        />
      )}

      {/* Left Sidebar — always expanded on desktop, drawer on mobile */}
      <aside
        className={`bg-surface border-r border-border flex flex-col shrink-0 fixed md:relative inset-y-0 left-0 z-50 md:z-auto pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] md:pt-0 md:pb-0 w-[272px] md:w-[228px] lg:w-[240px] xl:w-[clamp(248px,14vw,280px)] 2xl:w-[clamp(280px,14vw,320px)] ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'} transition-transform duration-200`}
        data-testid="startup-sidebar"
      >
        {/* Company identity lives here: compact, useful, and persistent across the startup workspace. */}
        <div className="px-2.5 pt-3 pb-2 xl:px-3 shrink-0" data-testid="startup-identity">
          <div
            className={`fb-company-identity-card relative w-full overflow-visible rounded-xl border text-left shadow-[0_1px_2px_rgba(15,23,42,0.04)] ${sidebarExpanded ? 'h-[72px] px-3.5 py-3' : 'h-[64px] px-2.5 py-3'}`}
            style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
            title={startup?.startup_name}
          >
            <div className="relative z-[1]">
              <div className={`flex items-center gap-3 ${sidebarExpanded ? '' : 'justify-center'}`}>
                {startup?.logo_url ? (
                  <img src={fileUrl(startup.logo_url)} className="h-[40px] w-[40px] shrink-0 rounded-lg border border-white/45 object-cover shadow-sm" alt="" />
                ) : (
                  <div className="flex h-[40px] w-[40px] shrink-0 items-center justify-center rounded-lg border border-[#99F6E4] bg-[#CCFBF1] shadow-sm dark:border-[#0D9488]/25 dark:bg-[#0D9488]/16">
                    <span className="font-mono text-[12px] font-bold text-[#0D9488] dark:text-teal-300">
                      {startup?.startup_name?.substring(0, 2).toUpperCase() || 'ST'}
                    </span>
                  </div>
                )}
                {sidebarExpanded && (
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-heading text-[16px] font-extrabold leading-[1.15] tracking-[-0.02em] text-text-primary" title={startup?.startup_name}>
                      {startup?.startup_name || 'Your Startup'}
                    </div>
                    {startupTag && (
                      <div className="mt-1 truncate text-[10px] font-medium text-text-muted" title={startupTag}>{startupTag}</div>
                    )}
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>

        <div className="px-3.5 py-4 flex-1 overflow-y-auto overflow-x-hidden">
          {navGroups.map((group, groupIndex) => (
            <nav
              key={group.key}
              className={`${groupIndex === 0 ? '' : 'mt-5 border-t border-border/70 pt-4'} space-y-1`}
              data-testid={`nav-group-${group.key}`}
            >
              {group.label && <SidebarSectionLabel expanded={sidebarExpanded}>{group.label}</SidebarSectionLabel>}
              {group.items.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                const locked = item.module && !isEnabled(item.module);
                return (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    title={sidebarExpanded ? undefined : item.label}
                    className={`fb-sidebar-nav-item relative w-full min-h-[44px] flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-150 ${
                      isActive
                        ? 'bg-[#0F766E] text-white shadow-[0_1px_2px_rgba(15,118,110,0.18)] dark:bg-[#0F766E] dark:text-white'
                        : 'text-text-muted hover:bg-[#F1F5F9] hover:text-text-primary'
                    }`}
                    data-testid={item.testId}
                    data-active={isActive ? 'true' : 'false'}
                  >
                    <Icon size={18} weight="bold" className="shrink-0" />
                    {sidebarExpanded && <span className="text-[13px] font-semibold flex-1 text-left truncate">{item.label}</span>}
                    {sidebarExpanded && locked && (
                      <span className="text-[9px] uppercase tracking-wider font-semibold text-text-muted" data-testid={`${item.testId}-soon`}>soon</span>
                    )}
                    {sidebarExpanded && item.badge && (
                      <span className="text-[10px] font-mono font-semibold text-[#10b981]">{item.badge}</span>
                    )}
                    {item.alert && (
                      sidebarExpanded ? (
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full flex-shrink-0 ${item.alertComplete ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : 'bg-warning/10 text-warning'}`}>{item.alertLabel}</span>
                      ) : (
                        <div className={`w-1.5 h-1.5 rounded-full shrink-0 absolute top-1 right-1 ${item.alertComplete ? 'bg-emerald-500' : 'bg-warning'}`} />
                      )
                    )}
                  </button>
                );
              })}
            </nav>
          ))}
        </div>

        <div className="px-3 pb-3 pt-2 shrink-0" data-testid="nav-group-general">
          <div className="fb-sidebar-account-card rounded-xl border p-1.5">
            <SidebarProfileMenu
              name={user?.full_name || startup?.founder_name || user?.email}
              expanded={sidebarExpanded}
              onLogout={handleLogout}
              basePath="/startup"
              coins={startup && isEnabled('startup.coins') ? Number(startup.coin_balance || 0) : null}
            />

            <div className="mt-1.5 flex items-center gap-2.5 border-t px-2.5 pt-2.5 pb-1.5" style={{ borderColor: 'var(--border)' }}>
              <img src="/founderboard-logo.png" alt="FounderBoard" className="h-6 w-6 object-contain" />
              <span className="text-[13px] font-heading font-extrabold tracking-[-0.02em] text-text-primary">FounderBoard</span>
              <span className="ml-auto text-[8px] font-mono uppercase tracking-[0.12em] opacity-50" style={{ color: 'var(--muted-foreground)' }}>Workspace</span>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Compact FounderBoard AI command surface. */}
        <main ref={mainScrollRef} className={`flex-1 overflow-y-auto ${['/startup', '/startup/', '/startup/dashboard'].includes(location.pathname) ? 'startup-dashboard-canvas' : ''}`}>
          <div className="fb-ai-sticky-shell sticky top-[env(safe-area-inset-top)] z-40 pb-2 pt-2 pointer-events-none md:top-0 md:pt-3">
            <div className="mx-auto w-full max-w-[1280px] px-3 sm:px-4 md:px-5 pointer-events-auto">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  aria-label="Open navigation"
                  aria-expanded={mobileSidebarOpen}
                  onClick={() => setMobileSidebarOpen(true)}
                  className="md:hidden flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-border bg-surface text-text-primary shadow-[0_1px_2px_rgba(15,23,42,0.06)] transition-colors hover:bg-muted"
                  data-testid="mobile-sidebar-trigger"
                >
                  <List size={21} weight="bold" />
                </button>
                <div className="min-w-0 flex-1">
                  <StartupAICommandBar user={user} startup={startup} />
                </div>
              </div>
            </div>
          </div>

          {/* Screen Content */}
          <div className={`startup-page-content ${['/startup', '/startup/', '/startup/dashboard'].includes(location.pathname) ? 'is-dashboard' : 'is-page'}`}><Routes>
            <Route path="/dashboard" element={<StartupDashboardNew data={dashboardData} user={user} />} />
            <Route path="/coach" element={<Navigate to="/startup/dashboard?ai=1" replace />} />
            <Route path="/benchmarks" element={<Benchmarks user={user} />} />
            <Route path="/equity" element={<EquityDashboard user={user} />} />
            <Route path="/update" element={<MonthlyUpdateForm user={user} onSuccess={fetchDashboardData} />} />
            <Route path="/programs" element={<StartupPrograms user={user} />} />
            <Route path="/services/*" element={<ModuleGate module="startup.services"><StartupServices /></ModuleGate>} />
            <Route path="/credit/*" element={<ModuleGate module="startup.credit"><StartupCredit /></ModuleGate>} />
            <Route path="/coins" element={<Navigate to="/startup/billing#coins" replace />} />
            <Route path="/studio" element={<Navigate to="/startup/marketing" replace />} />
            <Route path="/studio/agency/:agencyId" element={<NavigateWithParams to="/startup/marketing/agency/:agencyId" />} />
            <Route path="/studio/engagements/:engagementId" element={<NavigateWithParams to="/startup/marketing/engagements/:engagementId" />} />
            <Route path="/metrics" element={<Navigate to="/startup/marketing/metrics" replace />} />
            <Route path="/brand" element={<Navigate to="/startup/marketing/brand" replace />} />
            <Route path="/marketing/*" element={<ModuleGate module="startup.marketing"><MarketingShell user={user} /></ModuleGate>} />
            <Route path="/engagements" element={<Navigate to="/startup/services/engagements" replace />} />
            <Route path="/vault" element={<VaultDashboard />} />
            <Route path="/vault/:dataroomId" element={<DataroomView />} />
            <Route path="/interactions" element={<Interactions user={user} />} />
            <Route path="/team" element={<Team user={user} />} />
            <Route path="/billing" element={<Billing user={user} />} />
            <Route path="/profile" element={<StartupProfile user={user} />} />
            <Route path="/" element={<StartupDashboardNew data={dashboardData} user={user} />} />
          </Routes></div>
        </main>
      </div>
      <CelebrationOverlay />
      <OnboardingTour role="startup" />
      </div>
    </div>
  );
};

export default StartupAppNew;
